// The order ticket's placement lifecycle on its result slot.
//
// Product contract: a placement request must be visible from the moment it is
// dispatched, and every line it produces must identify the exact order that
// was attempted. Three honest states, one identity:
//
//   dispatch  → `placementPendingLine`  "Placing <attempt>…"        (neutral)
//   accepted  → `placementSuccessLine`  "Order placed: <attempt>"
//   rejected  → `placementFailureLine`  "Rejected <attempt> — <venue reason>"
//   unknown   → `placementFailureLine`  "ORDER UNCONFIRMED — <attempt>. …"
//   STP, 0    → `placementFailureLine`  "Not placed: cancelled by Kalshi self-trade protection; … — <attempt>"
//   STP, >0   → `placementSelfTradePartialLine`  "Filled F of N; remainder cancelled by self-trade protection — <attempt>" (amber)
//
// The attempt identity is fixed BEFORE the request, from the values actually
// sent, so every line names the same order even when the form has moved on
// under a slow response. It carries what a trader needs to tell exactly which
// order this was: quantity, side, limit price (or MKT), the backed team, and
// the matchup — or the raw ticker when the row has no team codes to offer.
//
// Nothing here is venue evidence. The pending line never claims acceptance;
// the board projection (accountStore's placement fence + placementBus) is the
// ONLY thing that paints a resting order, and it does so on the venue's word
// alone. An unknown outcome is deliberately not a rejection: the same
// client_order_id may be live, so the line keeps the identity, says the
// outcome is still being verified, and points at Open Orders before any retry.

import {
  OrderSubmissionUnknownError,
  SELF_TRADE_CANCEL_REFERENCE,
  SELF_TRADE_CANCELLED_MESSAGE,
  SelfTradeCancelledError,
  selfTradePartialMessage,
  type SelfTradeCancel,
} from '@/lib/trading/orderSubmissionOutcome';
import type { OrderResultLine } from './oscarResultPresentation';

export interface OrderAttemptIdentity {
  count: number;
  side: 'yes' | 'no';
  /** null = market order. */
  priceCents: number | null;
  /** The backed team's code, when the surface knows it. */
  teamCode: string | null;
  /** "AWAY @ HOME" (or a richer market label) when the surface knows it. */
  matchup: string | null;
  /** Always present — the fallback when no matchup can be named. */
  ticker: string;
}

/** Leading token of every unknown-outcome line, in the same register as the
 *  Polymarket producer's "ORDER UNCONFIRMED" (#549/#556) so both venues read
 *  the same way on the shared slot. */
export const ORDER_UNCONFIRMED_LABEL = 'ORDER UNCONFIRMED';

/** "AWAY @ HOME" from a row's codes, or null when either is missing. */
export function matchupLabel(row: { awayCode?: string | null; homeCode?: string | null }): string | null {
  const away = row.awayCode?.trim();
  const home = row.homeCode?.trim();
  return away && home ? `${away} @ ${home}` : null;
}

/**
 * e.g. "25 YES @ 48¢ · NYY (NYY @ BOS)", "25 YES @ MKT · NYY (NYY @ BOS)",
 * or with no matchup "25 YES @ 48¢ · KXMLBGAME-26SEP04NYYBOS-NYY".
 */
export function describeOrderAttempt(attempt: OrderAttemptIdentity): string {
  const price = attempt.priceCents == null ? 'MKT' : `${attempt.priceCents}¢`;
  const head = `${attempt.count} ${attempt.side.toUpperCase()} @ ${price}`;
  const team = attempt.teamCode?.trim() || null;
  if (attempt.matchup) {
    return team ? `${head} · ${team} (${attempt.matchup})` : `${head} · ${attempt.matchup}`;
  }
  return team ? `${head} · ${team} · ${attempt.ticker}` : `${head} · ${attempt.ticker}`;
}

/** Shown from local dispatch until the relay answers. Neutral tone, a polite
 *  status, never a claim about the venue. */
export function placementPendingLine(attempt: string): OrderResultLine {
  return { ok: false, severity: 'pending', msg: `Placing ${attempt}…` };
}

/** A multi-order (ladder) dispatch: one pending line for the batch. */
export function placementBatchPendingLine(
  count: number,
  side: 'yes' | 'no',
  where: string,
): OrderResultLine {
  return {
    ok: false,
    severity: 'pending',
    msg: `Placing ${count} ${side.toUpperCase()} order${count === 1 ? '' : 's'} · ${where}…`,
  };
}

/** The venue accepted the order. The board paints it through the fence. */
export function placementSuccessLine(attempt: string): OrderResultLine {
  return { ok: true, msg: `Order placed: ${attempt}` };
}

/**
 * The request ended without an acceptance.
 *
 *  - A definitive rejection keeps the venue's (or the relay limit gate's)
 *    reason verbatim beside the attempt identity.
 *  - An ambiguous outcome (`OrderSubmissionUnknownError`) is NOT called a
 *    rejection. It keeps the identity, says the outcome is still being
 *    verified, and points at Open Orders before any retry. It stays red, as
 *    the Polymarket unknown does (#556): amber would undercut the one
 *    instruction that matters.
 */
export function placementFailureLine(attempt: string, err: unknown): OrderResultLine {
  // #1229: Kalshi's self-trade prevention cancelled the order whole. A
  // definite non-placement, worded as the venue's own reason, with the
  // reference on hover. Red: nothing rests and the trader has to act on
  // the other side before pressing again.
  if (err instanceof SelfTradeCancelledError) {
    return {
      ok: false,
      severity: 'critical',
      msg: `${SELF_TRADE_CANCELLED_MESSAGE} — ${attempt}`,
      reference: SELF_TRADE_CANCEL_REFERENCE,
    };
  }
  if (err instanceof OrderSubmissionUnknownError) {
    return {
      ok: false,
      severity: 'critical',
      msg:
        `${ORDER_UNCONFIRMED_LABEL} — ${attempt}. Kalshi did not confirm this order and it may ` +
        'or may not be resting; the outcome is still being verified. Check Open Orders before retrying.',
    };
  }
  const reason = err instanceof Error && err.message ? err.message : 'Order failed';
  return { ok: false, severity: 'critical', msg: `Rejected ${attempt} — ${reason}` };
}

/**
 * #1229: some contracts filled before Kalshi's self-trade prevention cancelled
 * the remainder. Not an error (the fills are real) and not "Order placed"
 * (nothing rests): amber, with the exact numbers the relay reported.
 */
export function placementSelfTradePartialLine(attempt: string, cancel: SelfTradeCancel): OrderResultLine {
  return {
    ok: false,
    severity: 'warning',
    msg: `${selfTradePartialMessage(cancel)} — ${attempt}`,
    fill: { filledCount: cancel.filled, requestedCount: cancel.requested },
    reference: SELF_TRADE_CANCEL_REFERENCE,
  };
}
